// The ONE supplier picker (D86). Every screen PICKS a vendor from the vendor list through this
// component, so no free-typed supplier name can reach a form. Typing only filters the loaded
// list - `onChange` fires solely with a list entry (`{vendorId, name}`) or the cleared value
// `{vendorId: null, name: ''}`.
//
// D107 (reverses D86.1, "vendors are added only on the Vendors page"): a person who may create
// vendors sees "+ Create new vendor" at the foot of the open list. It opens the Vendors-page form
// in a popup (CreateVendorModal); the vendor it creates is selected. Everyone else is told to ask
// an admin. A picker is still never free text: the new vendor is a real, registered one.
//
// A saved value that is not on the list (an old typed name, `vendorId: null`, a vendor
// since deleted) is shown with a "not in vendor list" mark and reported through
// `onUnmatchedChange(true)`, so the host form can refuse to save until a real vendor is
// picked. The list loads once per mount (or comes from the host, `vendors`); filtering is in
// the browser. A confirmed-order line (D105.3) also lists its PRICED suppliers first
// (`priced`), each with its price, and every vendor after them.
import React, { useEffect, useLayoutEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchVendorOptions, type VendorOption } from '../lib/vendorsApi';
import { useOptionalAuth } from '../context/AuthContext';
import { hasScreenPermission } from '../lib/permissions';
import { CreateVendorModal } from '../features/vendors/CreateVendorModal';

export interface VendorSelection {
  vendorId: string | null;
  name: string;
}

export const NO_VENDOR: VendorSelection = { vendorId: null, name: '' };

interface VendorSelectProps {
  value: VendorSelection;
  onChange: (next: VendorSelection) => void;
  /** True while the shown value is a saved name that is not a vendor-list entry. */
  onUnmatchedChange?: (unmatched: boolean) => void;
  label?: string;
  id?: string;
  disabled?: boolean;
  /** Offer "no supplier" (emits NO_VENDOR). Default true. */
  allowClear?: boolean;
  placeholder?: string;
  className?: string;
  /** Grid-cell form (D98): h-8/text-xs, not-in-list shown as a red border + title (no paragraph), list in a fixed portal. */
  compact?: boolean;
  /**
   * The vendor list when the host already reads it (D105.3: the confirmed order's one read
   * of its lines' suppliers); null while the host is still loading it. Left out, it is fetched.
   */
  vendors?: VendorOption[] | null;
  /**
   * D105.3: suppliers with a price for this item, offered FIRST, each with a short note (its
   * price); the vendor list follows. A pick emits the row's own name (the price picked) with the
   * id of the vendor its `vendorId` names (D170.9), else of the one entry of the same name.
   */
  priced?: ReadonlyArray<{ name: string; note?: string; vendorId?: string | null }>;
  /** The combobox's name when no visible `label` is shown (a grid cell). */
  ariaLabel?: string;
  /**
   * D107: offer "+ Create new vendor" (to people with vendors:create) and "Ask an admin" (to the
   * rest) at the foot of the list. Default true.
   */
  allowCreate?: boolean;
  /**
   * What the create popup opens with when nothing is typed: the vendor an invoice printed. A
   * bare string is the name alone (every caller but one); an invoice's own parsed vendor also has
   * a phone, address and email already sitting on the record, which used to stop at this prop's
   * door — the popup it opens only ever saw a name, so a reviewer who had just read all four off
   * the invoice on the panel beside it retyped three of them from scratch. `VendorForm`'s `initial`
   * already took all four; only the plumbing between here and there was name-only.
   */
  createSuggestion?: string | { name?: string; phone?: string; address?: string; email?: string };
}

const norm = (s: string) => s.trim().toLowerCase();

/** One row of the open list: a priced supplier, or a vendor-list entry. */
type Entry = { name: string; note?: string; vendor?: VendorOption };

/** D107: the foot of the open list. A person with vendors:create gets the button; everyone else is told who to ask. */
const CreateFooter: React.FC<{ canCreate: boolean; onCreate: () => void }> = ({ canCreate, onCreate }) => (
  <li role="presentation" className="sticky bottom-0 border-t border-slate-200 bg-white px-3 py-2 text-xs">
    {canCreate ? (
      <button
        type="button"
        // Keep the focus in the box; the click still lands.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCreate}
        className="font-semibold text-primary-600 hover:underline"
      >
        + Create new vendor
      </button>
    ) : (
      <span className="text-slate-500">Not in the list? Ask an admin to add it.</span>
    )}
  </li>
);

export const VendorSelect: React.FC<VendorSelectProps> = ({
  value,
  onChange,
  onUnmatchedChange,
  label,
  id,
  disabled = false,
  allowClear = true,
  placeholder = 'Select a vendor…',
  className = '',
  compact = false,
  vendors,
  priced,
  ariaLabel,
  allowCreate = true,
  createSuggestion,
}) => {
  const [fetched, setFetched] = useState<VendorOption[] | null>(null);
  const hostLoads = vendors !== undefined;
  const base = hostLoads ? vendors : fetched;
  // Vendors created from this picker's own popup: listed at once (the host's list, or this
  // picker's one load, does not have them), so the new vendor is selected, never "not in the list".
  const [added, setAdded] = useState<VendorOption[]>([]);
  const options = useMemo(() => {
    if (!base || added.length === 0) return base;
    const fresh = added.filter((a) => !base.some((b) => b._id === a._id));
    return fresh.length ? [...base, ...fresh].sort((a, b) => a.name.localeCompare(b.name)) : base;
  }, [base, added]);
  const [creatingName, setCreatingName] = useState<string | null>(null); // the popup's starting name; null = closed
  // The rest of createSuggestion's fields, carried alongside the name above — see startCreate.
  const [creatingDetails, setCreatingDetails] = useState<{ phone?: string; address?: string; email?: string }>({});
  // Not usePermission: that throws without an AuthProvider, and a picker mounted on a screen with
  // none must still work. No sign-in context means no create button (the server enforces it anyway).
  const auth = useOptionalAuth();
  const canCreate = !!auth && hasScreenPermission(auth.user, 'vendors', 'create');
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState<string | null>(null); // null = not typing
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const autoId = useId();
  const inputId = id || `vendor-select-${autoId}`;
  const listboxId = `${inputId}-list`;

  useEffect(() => {
    if (hostLoads) return undefined;
    let cancelled = false;
    fetchVendorOptions()
      .then((rows) => {
        if (!cancelled) setFetched(rows);
      })
      .catch(() => {
        // No server prose on screen (invariant 6); the failure is said, not shown as "none".
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [hostLoads]);

  const hasValue = !!value.name.trim();
  // One naming style: a saved id the list knows is shown as that vendor's list name, whatever text was
  // saved beside it (an old line kept a vendor CODE - "KBSP", "WF" - next to full names on others, E3b).
  const shownName = options?.find((o) => o._id === value.vendorId)?.name ?? value.name;
  // Unknown until the list is here: never flag a saved vendor on a list we could not read.
  const unmatched =
    hasValue &&
    options !== null &&
    (!value.vendorId || !options.some((o) => o._id === value.vendorId));

  const onUnmatchedRef = useRef(onUnmatchedChange);
  onUnmatchedRef.current = onUnmatchedChange;
  useEffect(() => {
    onUnmatchedRef.current?.(unmatched);
  }, [unmatched]);

  // Compact: the list is fixed-positioned from the input's own rect, so a scroll box cannot clip it.
  useLayoutEffect(() => {
    if (!compact || !open) return undefined;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 224) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [compact, open]);

  // The priced suppliers first (D105.3), then every vendor not already among them; typing
  // filters both. A priced row is the vendor its id names (D170.9), else (D86.5) the ONE list entry
  // of its name; it keeps its own name, and two vendors of one name each stay pickable by their id.
  const { pricedRows, vendorRows } = useMemo(() => {
    const q = norm(query || '');
    const all = options || [];
    const named = (p: { name: string; vendorId?: string | null }) => {
      const byId = p.vendorId ? all.find((o) => o._id === p.vendorId) : undefined;
      if (byId) return byId;
      const same = all.filter((o) => norm(o.name) === norm(p.name));
      return same.length === 1 ? same[0] : undefined;
    };
    const carried = new Set((priced || []).map((p) => named(p)?._id).filter(Boolean));
    return {
      pricedRows: (priced || [])
        .filter((p) => !q || norm(p.name).includes(q))
        .map((p): Entry => ({ name: p.name, note: p.note, vendor: named(p) })),
      vendorRows: all
        .filter((o) => !carried.has(o._id))
        .filter((o) => !q || norm(o.name).includes(q) || norm(o.code || '').includes(q))
        .map((o): Entry => ({ name: o.name, vendor: o })),
    };
  }, [options, priced, query]);
  const filtered = useMemo(() => [...pricedRows, ...vendorRows], [pricedRows, vendorRows]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (boxRef.current && !boxRef.current.contains(t) && !listRef.current?.contains(t)) {
        setOpen(false);
        setQuery(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery(null);
  };

  const startCreate = () => {
    const suggestion = typeof createSuggestion === 'string' ? { name: createSuggestion } : createSuggestion || {};
    setCreatingName((query || '').trim() || (suggestion.name || '').trim());
    // Typed text winning over the suggestion (above) is about the NAME a person is actively
    // correcting; the invoice's own phone/address/email are not something typing a name
    // disagrees with, so they ride along regardless of what, if anything, was typed.
    setCreatingDetails({ phone: suggestion.phone, address: suggestion.address, email: suggestion.email });
    close();
  };

  const choose = (entry: Entry) => {
    onChange({ vendorId: entry.vendor ? entry.vendor._id : null, name: entry.name });
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (filtered[highlight]) choose(filtered[highlight]);
    }
  };

  const LIST = open ? (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={label || 'Vendors'}
          className={
            compact
              ? 'fixed z-50 max-h-64 overflow-auto rounded-md border border-slate-200 bg-white text-xs shadow-lg'
              : 'absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg'
          }
          style={compact && pos ? { top: pos.top, left: pos.left, width: pos.width } : undefined}
        >
          {options === null && !failed && (
            <li className="px-3 py-2 text-xs text-slate-500">Loading vendors…</li>
          )}
          {options !== null && filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-slate-500">
              {/* The list holds active vendors only (D86), so an inactive one is "not active", not "not there" (B059). */}
              {(query || '').trim() ? `No active vendor named "${(query || '').trim()}".` : 'No vendors in the list.'}
            </li>
          )}
          {filtered.map((entry, i) => {
            const picked = entry.vendor ? entry.vendor._id === value.vendorId : norm(entry.name) === norm(value.name);
            return (
              <React.Fragment key={`${entry.vendor ? entry.vendor._id : 'priced'}-${entry.name}`}>
                {priced && priced.length > 0 && (i === 0 || i === pricedRows.length) && (
                  <li role="presentation" className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {i < pricedRows.length ? 'Priced for this item' : 'All vendors'}
                  </li>
                )}
                <li
                  role="option"
                  aria-selected={picked}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(entry);
                  }}
                  className={`cursor-pointer px-3 py-2 text-sm ${i === highlight ? 'bg-slate-100' : ''} ${picked ? 'font-semibold' : ''}`}
                >
                  {entry.name}
                  {entry.note ? <span className="ml-2 text-xs text-slate-500">{entry.note}</span> : null}
                  {entry.vendor?.code ? <span className="ml-2 text-xs text-slate-400">{entry.vendor.code}</span> : null}
                </li>
              </React.Fragment>
            );
          })}
          {allowCreate && options !== null && <CreateFooter canCreate={canCreate} onCreate={startCreate} />}
        </ul>
  ) : null;

  return (
    <div className={`relative ${className}`} ref={boxRef}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1">
          {label}
        </label>
      )}
      <div className="flex items-center gap-1">
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label={label ? undefined : ariaLabel}
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-invalid={unmatched || undefined}
          title={compact && unmatched ? 'Not in vendor list - pick a vendor from the list to save.' : undefined}
          autoComplete="off"
          disabled={disabled}
          value={query !== null ? query : shownName}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          className={`w-full rounded-md border outline-none focus:border-primary-500 disabled:bg-slate-50 ${
            compact ? 'h-8 px-2 text-xs' : 'h-9 px-3 text-sm'
          } ${
            unmatched
              ? compact ? 'border-red-500 bg-red-50' : 'border-amber-400 bg-amber-50'
              : 'border-slate-200'
          }`}
        />
        {allowClear && hasValue && !disabled && (
          <button
            type="button"
            aria-label="Clear vendor"
            onClick={() => {
              onChange(NO_VENDOR);
              close();
            }}
            className={`${compact ? 'h-8' : 'h-9'} px-2 text-slate-400 hover:text-slate-700`}
          >
            ×
          </button>
        )}
      </div>

      {unmatched && !compact && (
        <p role="alert" className="mt-1 text-xs text-amber-700">
          Not in vendor list — pick a vendor from the list to save.
        </p>
      )}
      {failed && (
        <p role="alert" className="mt-1 text-xs text-red-600">
          The vendor list could not be loaded. Reload the page and try again.
        </p>
      )}

      {open && (compact ? (pos && createPortal(LIST, document.body)) : LIST)}

      {creatingName !== null && (
        <CreateVendorModal
          open
          initialName={creatingName}
          initialPhone={creatingDetails.phone}
          initialAddress={creatingDetails.address}
          initialEmail={creatingDetails.email}
          onClose={() => setCreatingName(null)}
          onCreated={(v) => {
            setAdded((prev) => [...prev, { _id: v._id, name: v.name }]);
            onChange({ vendorId: v._id, name: v.name });
          }}
        />
      )}

    </div>
  );
};

export default VendorSelect;
